import { useMemo } from 'preact/hooks';
import clsx from 'clsx';
import { useCurrentLocale } from '../../../i18n/useCurrentLocale';
import {
    getScheduleItemTypeCategory,
    type ScheduleItemTypeCategory,
    useGlobalScheduleQuery,
} from '../../../api/globalScheduleQuery';
import { hiddenSubjectsGlobalState } from '../../../state/queryParams/hiddenSubjects';
import { anchorPushStateHandler, useURLCreator } from '../../../state/queryParams/manager';
import { subjectDetailsModalSubjectGlobalState } from '../../../state/queryParams/subjectDetailsModal';
import { RoundIconButton } from '../../common/RoundIconButton';
import { MainViewSidebarSection } from './MainViewSidebarSection';

const getScheduleItemTypeCategoryClass = (category: ScheduleItemTypeCategory) => {
    switch (category) {
        case 'lecture':
            return 'text-sky-500';
        case 'exercise':
            return 'text-amber-500';
        case 'language':
            return 'text-green-500';
        case 'exam':
            return 'text-red-500';
        case 'special':
            return 'text-violet-500';
        default:
            return 'text-zinc-400';
    }
};

export const MainViewSidebarSubjectsSection = () => {
    const currentLocale = useCurrentLocale();
    const query = useGlobalScheduleQuery();

    const entries = useMemo(() => {
        const subjectToTypeToLecturerNames = {} as Record<string, Record<string, Set<string>>>;

        for (const item of query.data?.resource.aggregateSchedule.items ?? []) {
            subjectToTypeToLecturerNames[item.subject] = subjectToTypeToLecturerNames[item.subject] ?? {};
            subjectToTypeToLecturerNames[item.subject]![item.type.value] =
                subjectToTypeToLecturerNames[item.subject]![item.type.value] ?? new Set();
            for (const lecturer of item.lecturers) {
                subjectToTypeToLecturerNames[item.subject]![item.type.value]!.add(lecturer.name);
            }
        }

        return Object.entries(subjectToTypeToLecturerNames).map(([subject, typeToLecturerNames]) => ({
            subject,
            types: Object.entries(typeToLecturerNames).map(([itemType, lecturerNames]) => ({
                itemType,
                lecturerNames: Array.from(lecturerNames).sort((a, b) => a.localeCompare(b)),
            })),
        }));
    }, [query.data?.resource.aggregateSchedule.items]);

    const createDerivedURL = useURLCreator();

    const unnamedPlaceholderLabel = currentLocale.getLabel('main.sidebar.subjects.unnamedPlaceholder');

    if (entries.length === 0) {
        return null;
    }

    return (
        <MainViewSidebarSection title={currentLocale.getLabel('main.sidebar.subjects.sectionTitle')}>
            <div class="divide-x-main-bg-4 divide-y-2">
                {entries.map((entry) => (
                    <div key={entry.subject} class="flex flex-col py-1.5 transition-opacity">
                        <div class="flex items-center justify-between">
                            {entry.subject ? (
                                <p title={entry.subject}>{entry.subject}</p>
                            ) : (
                                <p class="italic" title={unnamedPlaceholderLabel}>
                                    {unnamedPlaceholderLabel}
                                </p>
                            )}

                            {entry.subject && (
                                <RoundIconButton
                                    class="text-x-main-text-muted h-8 p-1"
                                    icon="info"
                                    title={`${entry.subject} - ${currentLocale.getLabel('main.sidebar.subjects.details')}`}
                                    href={createDerivedURL(
                                        subjectDetailsModalSubjectGlobalState.createUpdate(entry.subject),
                                    )}
                                    onClick={anchorPushStateHandler}
                                />
                            )}
                        </div>

                        {entry.types.map((subEntry) => {
                            const isHidden = (query.params.hiddenSubjects[entry.subject] ?? []).includes(
                                subEntry.itemType,
                            );

                            return (
                                <div key={subEntry.itemType} class="my-1 flex items-center justify-between">
                                    <div class="border-l-x-main-bg-5 ml-1 flex flex-col border-l pl-1">
                                        <p
                                            class={clsx(
                                                'truncate text-sm',
                                                getScheduleItemTypeCategoryClass(
                                                    getScheduleItemTypeCategory(subEntry.itemType),
                                                ),
                                            )}
                                            title={subEntry.itemType}
                                        >
                                            {subEntry.itemType}
                                        </p>

                                        <div class="text-x-main-text-muted text-xxs flex flex-col">
                                            {subEntry.lecturerNames.map((lecturerName) => (
                                                <div key={lecturerName} class="truncate" title={lecturerName}>
                                                    {lecturerName}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <RoundIconButton
                                        class="text-x-main-text-supermuted h-8 p-1"
                                        icon={isHidden ? 'eyeHide' : 'eyeShow'}
                                        title={currentLocale.getLabel(
                                            isHidden
                                                ? 'main.sidebar.subjects.showXCTA'
                                                : 'main.sidebar.subjects.hideXCTA',
                                            {
                                                args: [
                                                    [
                                                        entry.subject || unnamedPlaceholderLabel,
                                                        subEntry.itemType || unnamedPlaceholderLabel,
                                                    ].join(' - '),
                                                ],
                                            },
                                        )}
                                        href={createDerivedURL(
                                            isHidden
                                                ? hiddenSubjectsGlobalState.createRemoveUpdate(
                                                      entry.subject,
                                                      subEntry.itemType,
                                                  )
                                                : hiddenSubjectsGlobalState.createAddUpdate(
                                                      entry.subject,
                                                      subEntry.itemType,
                                                  ),
                                        )}
                                        onClick={anchorPushStateHandler}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </MainViewSidebarSection>
    );
};
